import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Input, Select, Button, Alert, useIsMobile } from '../components/ui';
import LocationSelect from '../components/ui/LocationSelect';
import api from '../utils/api';

const ORG_TYPES = [
  { value: 'hospital', label: 'Hospital' },
  { value: 'ngo', label: 'NGO' },
  { value: 'blood_bank', label: 'Blood Bank' },
  { value: 'corporate', label: 'Corporate' },
  { value: 'other', label: 'Other' },
];

export default function OrgSetup() {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    name: '', org_type: '', registration_number: '', website: '', address: '',
  });
  const [place, setPlace] = useState(null);

  const set = k => e => { setError(''); setForm(f => ({ ...f, [k]: e.target.value })); };

  const handleSubmit = async () => {
    if (!form.name.trim()) { setError('Organization name is required.'); return; }
    setLoading(true); setError('');
    try {
      await api.post('/organizations/', {
        name: form.name.trim(),
        org_type: form.org_type || null,
        registration_number: form.registration_number.trim() || null,
        website: form.website.trim() || null,
        address: form.address.trim() || null,
        city_id: place?.city_id || null,
      });
      navigate('/org/dashboard');
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (e.response?.status === 400 && detail?.includes('already exists')) { navigate('/org/dashboard'); return; }
      setError(detail || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: 'calc(100vh - 60px)',
      background: 'var(--gray-50)',
      display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
      padding: isMobile ? '24px 16px' : '48px 16px',
    }}>
      <div style={{ width: '100%', maxWidth: '480px', animation: 'bb-fadeUp 0.5s ease both' }}>
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <div style={{ fontSize: '32px', marginBottom: '10px' }}>🏢</div>
          <h1 style={{ fontSize: '22px', fontWeight: 800, letterSpacing: '-0.03em', marginBottom: '6px', color: 'var(--gray-900)' }}>
            Set up your organization
          </h1>
          <p style={{ fontSize: '13.5px', color: 'var(--gray-500)', lineHeight: 1.6 }}>
            Required before you can create donation camps
          </p>
        </div>

        <Card>
          <Input label="Organization name *" value={form.name} onChange={set('name')} placeholder="e.g. City Blood Bank" />
          <Select label="Organization type" value={form.org_type} onChange={set('org_type')}>
            <option value="">Select type (optional)</option>
            {ORG_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </Select>
          <Input label="Registration number (optional)" value={form.registration_number} onChange={set('registration_number')} />
          <Input label="Website (optional)" value={form.website} onChange={set('website')} placeholder="https://" />
          <Input label="Address (optional)" value={form.address} onChange={set('address')} />
          <LocationSelect onChange={setPlace} labels={{ city: 'City (optional)' }} />

          {error && <Alert variant="error">{error}</Alert>}

          <Alert variant="info" style={{ marginBottom: '16px' }}>
            New organizations are reviewed by an admin before they can create camps. You'll see your status on the dashboard.
          </Alert>

          <Button variant="primary" fullWidth loading={loading} onClick={handleSubmit}>
            Save organization
          </Button>
        </Card>
      </div>
    </div>
  );
}
